import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";

const LANG_BADGE = { en: "EN", te: "తె", hi: "हि" };

export default function AiChats() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("");
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/ai/conversations${status ? `?status=${status}` : ""}`)
      .then((r) => setList(r.data)).catch(() => toast.error("Failed to load conversations"));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const open = async (c) => {
    setSel(c.id);
    try {
      const r = await api.get(`/admin/ai/conversations/${c.id}`);
      setDetail(r.data);
      setAssignee(r.data.conversation.assigned_to || "");
    } catch { toast.error("Failed to load conversation"); }
  };

  const act = async (path, body, msg) => {
    try {
      await api.post(`/admin/ai/conversations/${sel}/${path}`, body);
      toast.success(msg);
      open({ id: sel });
      load();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4 sm:p-6 lg:flex-row" data-testid="ai-chats-page">
      <div className="w-full shrink-0 lg:w-96">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="font-heading text-xl font-bold text-white">AI Conversations</h1>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ai-chats-status-filter"
                  className="rounded-lg border border-adminborder bg-admincard px-2 py-1.5 text-xs text-slate-200">
            <option value="">All</option><option value="open">Open</option><option value="closed">Closed</option>
          </select>
        </div>
        <div className="space-y-2 overflow-y-auto" data-testid="ai-chats-list">
          {list.length === 0 && <p className="text-sm text-slate-500">No conversations yet.</p>}
          {list.map((c) => (
            <button key={c.id} onClick={() => open(c)} data-testid={`ai-chat-row-${c.id}`}
                    className={`w-full rounded-xl border p-3 text-left transition-colors ${sel === c.id ? "border-saffron bg-saffron/10" : "border-adminborder bg-admincard hover:border-slate-600"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-semibold text-white">
                  {c.customer?.name || c.customer?.phone || `Visitor ${String(c.session_id || "").slice(0, 6)}`}
                </p>
                <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-slate-300">
                  {LANG_BADGE[c.language] || c.language}
                </span>
              </div>
              <p className="mt-0.5 truncate text-xs text-slate-400">{c.last_message || "—"}</p>
              <p className="mt-1 text-[10px] text-slate-500">
                {(c.intents || []).join(", ") || "chat"} · {c.status} · {String(c.updated_at || "").slice(0, 16).replace("T", " ")}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="min-w-0 flex-1 rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-chat-detail">
        {!detail ? (
          <p className="py-16 text-center text-sm text-slate-500">Select a conversation to view the full chat history.</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-adminborder pb-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white">
                  {detail.conversation.customer?.name || "Website Visitor"}
                  {detail.conversation.customer?.phone ? <span className="ml-2 font-mono text-xs text-saffron">{detail.conversation.customer.phone}</span> : null}
                </p>
                <p className="text-[11px] text-slate-500">
                  ID {detail.conversation.id} · {detail.conversation.channel} · {detail.conversation.status}
                  {detail.lead ? ` · Lead: ${detail.lead.classification} (${detail.lead.status})` : ""}
                </p>
              </div>
              {detail.conversation.customer?.phone && (
                <a href={`https://wa.me/91${detail.conversation.customer.phone}`} target="_blank" rel="noreferrer"
                   data-testid="ai-chat-whatsapp-btn"
                   className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-500">WhatsApp</a>
              )}
              <button onClick={() => act("status", { status: detail.conversation.status === "open" ? "closed" : "open" }, "Status updated")}
                      data-testid="ai-chat-toggle-status-btn"
                      className="rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-200 hover:bg-white/5">
                {detail.conversation.status === "open" ? "Close" : "Re-open"}
              </button>
            </div>

            <div className="max-h-80 space-y-2 overflow-y-auto pr-1" data-testid="ai-chat-messages">
              {detail.messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-express/20 text-blue-100" : "bg-white/5 text-slate-200"}`}>
                    <p className="whitespace-pre-line">{m.content}</p>
                    <p className="mt-0.5 text-[9px] text-slate-500">{String(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 flex gap-2 border-t border-adminborder pt-3">
              <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Assign to employee…"
                     data-testid="ai-chat-assign-input"
                     className="w-40 rounded-lg border border-adminborder bg-adminbg px-3 py-1.5 text-xs text-slate-200 outline-none focus:border-saffron" />
              <button onClick={() => act("assign", { assignee }, "Assigned")} data-testid="ai-chat-assign-btn"
                      className="rounded-lg bg-saffron px-3 py-1.5 text-xs font-bold text-white hover:bg-saffron-hover">Assign</button>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note…"
                     data-testid="ai-chat-note-input"
                     className="min-w-0 flex-1 rounded-lg border border-adminborder bg-adminbg px-3 py-1.5 text-xs text-slate-200 outline-none focus:border-saffron" />
              <button onClick={() => { if (note.trim()) { act("note", { note }, "Note added"); setNote(""); } }}
                      data-testid="ai-chat-note-btn"
                      className="rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-200 hover:bg-white/5">Add Note</button>
            </div>

            {(detail.conversation.notes || []).length > 0 && (
              <div className="mt-3 space-y-1" data-testid="ai-chat-notes">
                {detail.conversation.notes.map((n, i) => (
                  <p key={i} className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-slate-400">
                    <span className="font-semibold text-slate-300">{n.by}:</span> {n.text}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
